'use client';

import React from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { usePartner } from '@/presentation/hooks/usePartner';

/**
 * PartnerSlider - Dynamic homepage slider based on partner detection
 *
 * Displays partner-specific branding and messaging.
 * Uses usePartner hook for hostname-based detection.
 *
 * Partners:
 * - MTN: PaySmallSmall messaging with MTN branding
 * - Telecel: PaySmallSmall messaging with Telecel branding
 * - Ecobank: Buy Now Pay Later messaging
 * - Motito: Generic marketplace messaging with Shop Now CTA
 */
export function PartnerSlider() {
  const { partner, partnerKey, isLoading } = usePartner();

  // Show skeleton during loading
  if (isLoading) {
    return (
      <div className="relative flex min-h-[188px] animate-pulse flex-col items-start justify-center bg-gray-300 pl-6 sm:h-96 lg:min-h-[390px]" />
    );
  }

  const { slider, logo } = partner;

  return (
    <div
      className="relative flex min-h-[188px] flex-col items-start justify-center pl-6 text-white sm:h-96 sm:p-5 sm:pl-10 md:pl-12 lg:min-h-[390px] lg:pl-16"
      style={{ backgroundColor: slider?.backgroundColor || '#004F71' }}
    >
      {/* Slider background image */}
      <Image
        src={slider?.image || '/assets/images/mtn-slider-image.png'}
        width={576}
        height={396}
        className="object-center absolute right-0 top-0 h-full w-2/5 object-cover md:w-1/2"
        alt="Slider Image"
        quality={100}
      />

      {/* Title */}
      <h1 className="leading mb-2 font-garnettBlack text-lg sm:text-4xl md:text-5xl lg:text-6xl">
        {slider?.title || 'PaySmallSmall'}
      </h1>

      {/* Subtitle - only show if exists */}
      {slider?.subtitle && (
        <h4 className="font-garnettBlack text-base font-medium md:text-2xl lg:text-4xl">
          {slider.subtitle.split('<br />').map((line, i, arr) => (
            <React.Fragment key={i}>
              {line}
              {i < arr.length - 1 && <br />}
            </React.Fragment>
          ))}
        </h4>
      )}

      {/* Description - only show on desktop if exists */}
      {slider?.description && (
        <p className="hidden max-w-[40%] md:block md:text-base lg:text-lg">
          {slider.description}
        </p>
      )}

      {/* Shop Now button - only for generic Motito */}
      {slider?.showButton && (
        <Link href="/" className="mt-4">
          <button className="rounded-full bg-white px-6 py-2 font-medium text-gray-900 transition hover:bg-gray-100">
            Shop Now
          </button>
        </Link>
      )}

      {/* Partner logo - show for partner domains */}
      {partnerKey !== 'motito' && (
        <Image
          className="mt-4 w-[50px]"
          width={100}
          height={70}
          src={logo}
          alt={`${partnerKey} logo`}
        />
      )}

      {/* Decorative vectors */}
      {partnerKey === 'mtn' && (
        <Image
          src="/assets/svgs/mtn-vector.svg"
          width={137}
          height={131}
          className="absolute right-0 top-0 w-16 text-red-300 md:w-32"
          alt="decoration"
          quality={100}
        />
      )}
      {partnerKey === 'motito' && (
        <>
          <Image
            src="/assets/svgs/Vector 1.svg"
            width={137}
            height={131}
            className="absolute right-0 top-0 w-16 md:w-32"
            alt="decoration"
            quality={100}
          />
          <Image
            src="/assets/svgs/Vector 2.svg"
            width={118}
            height={94}
            className="absolute bottom-0 left-0 w-16 md:w-32"
            alt="decoration"
            quality={100}
          />
        </>
      )}
    </div>
  );
}

export default PartnerSlider;
